import { AUDIO_FILES, AUDIO_LABELS, readAudioClips, saveAudioClip, saveBackgroundAudio } from './AudioLibrary';
import type { AttackStyle } from '../game/combat';
import type { WeaponType } from '../game/weapons';

const MAX_BACKGROUND_AUDIO_BYTES = 50 * 1024 * 1024;
const NETWORK_AUDIO = new Set(['bark', 'kick', 'pass', 'bounce', 'post', 'combatSwing', 'combatImpact', 'combatLanding', 'weaponSwing', 'weaponThrow', 'weaponImpact', 'shotgun', 'rocketLaunch', 'rocketExplosion', 'pickup', 'whistle', 'down', 'explosion', 'bombExplosion', 'reactToHit', 'goalHorn']);

export function validateBackgroundAudioFile(file: File): void {
  if (!/\.mp3$/i.test(file.name)) throw new Error('El archivo seleccionado debe tener formato MP3 y terminar en .mp3.');
  if (file.size === 0) throw new Error('El archivo MP3 está vacío.');
  if (file.size > MAX_BACKGROUND_AUDIO_BYTES) {
    throw new Error(`El archivo pesa ${(file.size / 1024 / 1024).toFixed(1)} MB; el máximo es 50 MB. Oracle no recibió el archivo.`);
  }
}

export class AudioSystem {
  onNetworkEvent: ((name: string, args: unknown[]) => void) | null = null;
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private readonly audioBuffers = new Map<string, AudioBuffer>();
  private voiceUntil = 0;
  private goalVoiceUntil = 0;
  private readonly replacements = new Map<string, AudioBuffer>();
  private background: HTMLAudioElement | null = null;
  private music: HTMLAudioElement | null = null;
  private readonly musicUrls = new Map<'endMusic' | 'menuMusic', string>();
  private musicVersion = 0;
  private musicId: 'endMusic' | 'menuMusic' | null = null;
  private readonly resumeMusicFromGesture = () => {
    if (this.music?.paused && !this.music.ended && !this.paused) void this.music.play().catch(() => undefined);
  };
  private backgroundWanted = false;
  private backgroundName = '';
  private readonly backgroundEndpoint = `${import.meta.env.BASE_URL}api/audio/background`;
  private readonly clipEndpoint = (id: string) => `${import.meta.env.BASE_URL}api/audio/clips/${encodeURIComponent(id)}`;
  private readonly backgroundLoading: Promise<void>;
  private loading: Promise<void> | null = null;
  private customCharge: AudioBufferSourceNode | null = null;
  private disposed = false;
  private paused = false;
  private muted = false;
  private sfxVolume = 1;
  private musicVolume = 1;
  private unlockPromise: Promise<void> | null = null;
  private readonly sources = new Set<AudioScheduledSourceNode>();
  private readonly voiceSources = new Set<AudioScheduledSourceNode>();
  private readonly combatTimes = new Map<string, number>();
  private nextExplosionSecond = false;
  private readonly unlockFromGesture = () => { void this.unlock().catch(() => undefined); };
  private chargeOsc: OscillatorNode | null = null;
  private chargeGain: GainNode | null = null;

  constructor() {
    let networkDepth = 0;
    const methods = this as unknown as Record<string, (...args: unknown[]) => void>;
    for (const name of NETWORK_AUDIO) {
      const original = methods[name].bind(this);
      methods[name] = (...args: unknown[]) => {
        if (networkDepth === 0) this.onNetworkEvent?.(name, args);
        networkDepth++;
        try { original(...args); } finally { networkDepth--; }
      };
    }
    window.addEventListener('pointerdown', this.resumeMusicFromGesture);
    window.addEventListener('keydown', this.resumeMusicFromGesture);
    window.addEventListener('pointerdown', this.unlockFromGesture);
    window.addEventListener('keydown', this.unlockFromGesture);
    this.backgroundLoading = this.refreshBackground().then(() => undefined, () => undefined);
  }

  playNetworkEvent(name: string, args: unknown[]): void {
    if (!NETWORK_AUDIO.has(name) || !Array.isArray(args) || args.length > 3 ||
      args.some(arg => arg !== null && !['string', 'number', 'boolean'].includes(typeof arg))) return;
    const methods = this as unknown as Record<string, (...args: unknown[]) => void>;
    methods[name](...args);
  }

  private setBackgroundSource(available: boolean, name = ''): void {
    if (this.background) { this.background.pause(); this.background.removeAttribute('src'); this.background.load(); }
    this.backgroundName = name;
    if (!available) { this.background = null; return; }
    const audio = this.background ?? new Audio();
    audio.loop = true;
    audio.preload = 'none';
    audio.volume = 0.24 * this.musicVolume;
    audio.muted = this.muted;
    audio.src = `${this.backgroundEndpoint}?v=${Date.now()}`;
    this.background = audio;
    if (this.backgroundWanted && !this.paused) void audio.play().catch(() => undefined);
  }

  getBackgroundName(): string { return this.backgroundName; }

  async refreshBackground(): Promise<string> {
    const response = await fetch(`${this.backgroundEndpoint}/meta`, { cache: 'no-store' });
    if (!response.ok) throw new Error('No se pudo consultar el audio guardado en Oracle.');
    const info = await response.json() as { available: boolean; name?: string };
    this.setBackgroundSource(info.available, info.name ?? '');
    return this.backgroundName;
  }

  async replaceBackground(file: File | null, token: string, onProgress: (percent: number) => void): Promise<void> {
    if (file) validateBackgroundAudioFile(file);
    if (!token.trim()) throw new Error('Ingresa la clave de creador Oracle.');
    await new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open(file ? 'PUT' : 'DELETE', this.backgroundEndpoint);
      request.setRequestHeader('Authorization', `Bearer ${token.trim()}`);
      if (file) {
        request.setRequestHeader('Content-Type', 'audio/mpeg');
        request.setRequestHeader('X-File-Name', encodeURIComponent(file.name));
      }
      request.upload.onprogress = event => { if (event.lengthComputable) onProgress(Math.min(99, Math.round(event.loaded / event.total * 100))); };
      request.onerror = () => reject(new Error('No se pudo conectar con Oracle.'));
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) { onProgress(100); resolve(); return; }
        let message = 'Oracle rechazó la operación.';
        try { message = (JSON.parse(request.responseText) as { error?: string }).error ?? message; } catch { /* Keep the readable fallback. */ }
        reject(new Error(request.status === 401 ? 'Clave de creador incorrecta.' : request.status === 413 ? 'Oracle rechazó el archivo porque supera el límite de 50 MB.' : message));
      };
      request.send(file);
    });
    try { await saveBackgroundAudio(null); } catch { /* Oracle remains the source of truth. */ }
    this.backgroundWanted = file ? this.backgroundWanted : false;
    await this.refreshBackground();
  }

  async clipInfo(id: string): Promise<{ available: boolean; name?: string; size?: number }> {
    if (!AUDIO_LABELS[id]) throw new Error('Sonido desconocido.');
    const response = await fetch(`${this.clipEndpoint(id)}/meta`, { cache: 'no-store' });
    if (!response.ok) throw new Error('No se pudo consultar el sonido en Oracle.');
    return response.json();
  }

  async uploadClip(id: string, file: File, token: string, onProgress: (percent: number) => void): Promise<void> {
    if (!AUDIO_LABELS[id]) throw new Error('Sonido desconocido.');
    if (id === 'endMusic' || id === 'menuMusic') validateBackgroundAudioFile(file);
    else if (!/\.mp3$/i.test(file.name) || file.size === 0 || file.size > 10 * 1024 * 1024) throw new Error('Selecciona un MP3 de hasta 10 MB.');
    if (!token.trim()) throw new Error('Ingresa la clave de creador Oracle.');
    await new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('PUT', this.clipEndpoint(id));
      request.setRequestHeader('Authorization', `Bearer ${token.trim()}`);
      request.setRequestHeader('Content-Type', 'audio/mpeg');
      request.setRequestHeader('X-File-Name', encodeURIComponent(file.name));
      request.upload.onprogress = event => { if (event.lengthComputable) onProgress(Math.min(99, Math.round(event.loaded / event.total * 100))); };
      request.onerror = () => reject(new Error('No se pudo conectar con Oracle.'));
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) { onProgress(100); resolve(); return; }
        let message = 'Oracle rechazó el sonido.';
        try { message = (JSON.parse(request.responseText) as { error?: string }).error ?? message; } catch { /* Keep the readable fallback. */ }
        reject(new Error(request.status === 401 ? 'Clave de creador incorrecta.' : message));
      };
      request.send(file);
    });
    try { await saveAudioClip(id, null); } catch { /* Oracle takes precedence over stale browser copies. */ }
  }

  startBackground(): void {
    this.stopMusic();
    this.backgroundWanted = true;
    void this.backgroundLoading.then(() => { if (this.backgroundWanted && this.background && !this.paused) void this.background.play().catch(() => undefined); });
  }

  stopBackground(): void {
    this.backgroundWanted = false;
    if (this.background) { this.background.pause(); this.background.currentTime = 0; }
  }

  startEndMusic(): Promise<void> { return this.startMusic('endMusic'); }

  startMenuMusic(): Promise<void> {
    if (this.musicId === 'menuMusic') return Promise.resolve();
    return this.startMusic('menuMusic');
  }

  private async startMusic(id: 'endMusic' | 'menuMusic'): Promise<void> {
    this.stopMusic();
    this.stopBackground();
    this.musicId = id;
    const version = this.musicVersion;
    let src = this.musicUrls.get(id);
    let audio: HTMLAudioElement | null = null;
    let playResult: Promise<unknown> | null = null;
    if (!src) {
      try {
        // Start the media request immediately on the match-end transition. A
        // metadata round-trip first can miss the browser's short autoplay window.
        src = `${this.clipEndpoint(id)}?v=${Date.now()}`;
        audio = this.createMusicElement(id, src);
        this.music = audio;
        if (!this.paused) playResult = audio.play().catch(error => error);
        const [info, playbackError] = await Promise.all([
          this.clipInfo(id).catch(() => null),
          playResult ?? Promise.resolve(null),
        ]);
        if (version !== this.musicVersion || this.disposed) return;
        if (info?.available) {
          if (playbackError && (playbackError as DOMException).name !== 'NotAllowedError') {
            const local = (await readAudioClips()).find(clip => clip.id === id);
            if (version !== this.musicVersion || this.disposed) return;
            if (local) {
              audio.pause(); audio.removeAttribute('src'); audio.load();
              src = URL.createObjectURL(local.blob); this.musicUrls.set(id, src);
              audio = this.createMusicElement(id, src); this.music = audio;
              if (!this.paused) await audio.play().catch(() => undefined);
            }
          }
          return;
        }
        audio.pause(); audio.removeAttribute('src'); audio.load();
        const local = (await readAudioClips()).find(clip => clip.id === id);
        if (version !== this.musicVersion || this.disposed || !local) { this.music = null; return; }
        src = URL.createObjectURL(local.blob); this.musicUrls.set(id, src);
        audio = null;
        playResult = null;
      } catch {
        if (version === this.musicVersion && this.music === audio) this.music = null;
        return;
      }
    }
    if (!src || version !== this.musicVersion || this.disposed) return;
    audio = this.createMusicElement(id, src);
    this.music = audio;
    if (!this.paused && !playResult) await audio.play().catch(() => undefined);
  }

  private createMusicElement(id: 'endMusic' | 'menuMusic', src: string): HTMLAudioElement {
    const audio = new Audio(src);
    audio.loop = id === 'menuMusic';
    audio.volume = 0.24 * this.musicVolume;
    audio.muted = this.muted;
    return audio;
  }

  stopMusic(): void {
    this.musicVersion++;
    this.musicId = null;
    if (this.music) {
      this.music.pause();
      this.music.removeAttribute('src');
      this.music.load();
      this.music = null;
    }
  }

  async unlock(): Promise<void> {
    if (this.disposed || this.paused) return;
    if (this.unlockPromise) return this.unlockPromise;
    if (this.context) {
      this.unlockPromise = this.context.resume();
      try {
        await this.unlockPromise;
        if (!this.disposed && this.context?.state === 'running') this.removeUnlockListeners();
      } finally { this.unlockPromise = null; }
      return;
    }
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    this.context = new AudioContextClass();
    this.master = this.context.createGain();
    this.master.gain.value = this.muted ? 0 : 0.48 * this.sfxVolume;
    const limiter = this.context.createDynamicsCompressor();
    limiter.threshold.value = -14;
    limiter.knee.value = 8;
    limiter.ratio.value = 16;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.14;
    this.master.connect(limiter).connect(this.context.destination);

    const length = this.context.sampleRate;
    this.noiseBuffer = this.context.createBuffer(1, length, this.context.sampleRate);
    const data = this.noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;

    this.unlockPromise = this.context.resume();
    try {
      await this.unlockPromise;
      if (!this.disposed && this.context?.state === 'running') this.removeUnlockListeners();
    } finally { this.unlockPromise = null; }
    this.loading = this.loadGeneratedAudio();
  }

  private get ready(): boolean {
    return !this.disposed && !this.paused && !this.muted && !!this.context && this.context.state === 'running' && !!this.master;
  }

  private removeUnlockListeners(): void {
    window.removeEventListener('pointerdown', this.unlockFromGesture);
    window.removeEventListener('keydown', this.unlockFromGesture);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) this.chargeStop();
    if (this.context && this.master) this.master.gain.setTargetAtTime(muted ? 0 : 0.48 * this.sfxVolume, this.context.currentTime, 0.02);
    if (this.background) this.background.muted = muted;
    if (this.music) this.music.muted = muted;
  }

  setVolumes(sfx: number, music: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, Number.isFinite(sfx) ? sfx : 1));
    this.musicVolume = Math.max(0, Math.min(1, Number.isFinite(music) ? music : 1));
    if (this.context && this.master) this.master.gain.setTargetAtTime(this.muted ? 0 : 0.48 * this.sfxVolume, this.context.currentTime, 0.02);
    if (this.background) this.background.volume = 0.24 * this.musicVolume;
    if (this.music) this.music.volume = 0.24 * this.musicVolume;
  }

  getMuted(): boolean { return this.muted; }

  setPaused(paused: boolean): void {
    this.paused = paused;
    if (paused) {
      this.chargeStop();
      this.background?.pause();
      this.music?.pause();
      if (this.context?.state === 'running') void this.context.suspend().catch(() => undefined);
    } else {
      void this.unlock().catch(() => undefined);
      if (this.backgroundWanted && this.background) void this.background.play().catch(() => undefined);
      if (this.music && !this.music.ended) void this.music.play().catch(() => undefined);
    }
  }

  private track(source: AudioScheduledSourceNode, nodes: AudioNode[], voice = false): void {
    this.sources.add(source);
    if (voice) this.voiceSources.add(source);
    source.onended = () => {
      this.sources.delete(source);
      this.voiceSources.delete(source);
      source.disconnect();
      for (const node of nodes) node.disconnect();
    };
  }

  private async loadGeneratedAudio(): Promise<void> {
    const context = this.context;
    if (!context || this.audioBuffers.size) return;
    await Promise.all(Object.entries(AUDIO_FILES).map(async ([id, file]) => {
      try {
        const response = await fetch(`${import.meta.env.BASE_URL}audio/${file}.mp3?v=goal-shouts-v3-20261019-r2`);
        if (response.ok && !this.disposed) this.audioBuffers.set(id, await context.decodeAudioData(await response.arrayBuffer()));
      } catch { /* Generated audio is optional; synthesized effects remain available. */ }
    }));
    try {
      const catalogResponse = await fetch(`${import.meta.env.BASE_URL}api/audio/clips/meta`, { cache: 'no-store' });
      if (catalogResponse.ok) {
        const catalog = await catalogResponse.json() as { clips?: Record<string, unknown> };
        await Promise.all(Object.keys(catalog.clips ?? {}).filter(id => AUDIO_LABELS[id] && id !== 'endMusic' && id !== 'menuMusic').map(async id => {
          try {
            const response = await fetch(this.clipEndpoint(id), { cache: 'no-store' });
            if (response.ok && !this.disposed) this.replacements.set(id, await context.decodeAudioData(await response.arrayBuffer()));
          } catch { /* A broken custom clip falls back to the original sound. */ }
        }));
      }
    } catch { /* Oracle is optional; packaged and synthesized sounds remain available. */ }
    try {
      const clips = await readAudioClips();
      await Promise.all(clips.map(async clip => {
        if (clip.id === 'endMusic' || clip.id === 'menuMusic') return;
        try {
          const buffer = await context.decodeAudioData(await clip.blob.arrayBuffer());
          if (!this.disposed && !this.replacements.has(clip.id)) this.replacements.set(clip.id, buffer);
        } catch { /* One damaged saved clip must not disable other sounds. */ }
      }));
    } catch { /* Original sounds remain available without browser storage. */ }
  }

  async prepare(): Promise<void> {
    await this.unlock();
    await this.loading;
  }

  async replace(id: string, file: File | null): Promise<void> {
    if (!AUDIO_LABELS[id]) throw new Error('Sonido desconocido.');
    if (id === 'endMusic' || id === 'menuMusic') {
      if (file) {
        validateBackgroundAudioFile(file);
        const header = new Uint8Array(await file.slice(0, 3).arrayBuffer());
        if (!(header[0] === 0x49 && header[1] === 0x44 && header[2] === 0x33) && !(header[0] === 0xff && (header[1] & 0xe0) === 0xe0)) throw new Error('El archivo no contiene audio MP3 compatible.');
      }
      await saveAudioClip(id, file ? { id, name: file.name, blob: file } : null);
      const restart = this.musicId === id;
      if (restart) this.stopMusic();
      const previous = this.musicUrls.get(id);
      if (previous) URL.revokeObjectURL(previous);
      if (file) this.musicUrls.set(id, URL.createObjectURL(file));
      else this.musicUrls.delete(id);
      if (restart) void this.startMusic(id);
      return;
    }
    await this.loading;
    if (!file) {
      await saveAudioClip(id, null);
      this.replacements.delete(id);
      if (this.context) {
        try {
          const response = await fetch(this.clipEndpoint(id), { cache: 'no-store' });
          if (response.ok && !this.disposed) this.replacements.set(id, await this.context.decodeAudioData(await response.arrayBuffer()));
        } catch { /* The packaged sound remains available if Oracle cannot be read. */ }
      }
      return;
    }
    if (!/\.mp3$/i.test(file.name) || file.size === 0 || file.size > 10 * 1024 * 1024) throw new Error('Selecciona un MP3 de hasta 10 MB.');
    const decoder = new AudioContext();
    let buffer: AudioBuffer;
    try { buffer = await decoder.decodeAudioData(await file.arrayBuffer()); }
    catch { throw new Error('El archivo no contiene audio compatible.'); }
    finally { await decoder.close(); }
    if (buffer.duration > 30) throw new Error('El audio debe durar como máximo 30 segundos.');
    await saveAudioClip(id, { id, name: file.name, blob: file });
    this.replacements.set(id, buffer);
  }

  private custom(id: string): boolean {
    if (!this.replacements.has(id)) return false;
    this.playAsset(id, 0.9);
    return true;
  }

  preview(id: string): void {
    if (id === 'endMusic') { void this.startEndMusic(); return; }
    if (id === 'menuMusic') { void this.startMenuMusic(); return; }
    if (AUDIO_FILES[id]) { this.playAsset(id, 0.9); return; }
    const [event, kind] = id.split(':');
    if (event === 'swing') this.combatSwing(kind as AttackStyle);
    else if (event === 'impact') this.combatImpact(kind as AttackStyle);
    else if (event === 'weaponSwing') this.weaponSwing(kind as WeaponType);
    else if (event === 'weaponThrow') this.weaponThrow(kind as WeaponType);
    else if (event === 'weaponImpact' || event === 'weaponBroken') this.weaponImpact(kind as WeaponType, event === 'weaponBroken');
    else if (event === 'whistle') this.whistle(kind as 'short' | 'double' | 'long');
    else if (id === 'shot') this.kick(1);
    else if (id === 'bounce') this.bounce(1);
    else if (id === 'landing') this.combatLanding();
    else if (id === 'horn') this.goalHorn();
    else if (id === 'charge') { this.chargeStart(); this.chargeUpdate(1); }
    else if (id === 'pass') this.pass();
    else if (id === 'post') this.post();
    else if (id === 'slide') this.slide();
    else if (id === 'down') this.down();
    else if (id === 'explosion' || id === 'explosion2') this.explosionLayer(id);
    else if (id === 'pickup') this.pickup();
    else if (id === 'shotgun') this.shotgun();
    else if (id === 'menuClick') this.menuClick();
  }

  private playAsset(id: string, volume: number, voice = false): number {
    const context = this.context;
    const buffer = this.replacements.get(id) ?? this.audioBuffers.get(id);
    if (!this.ready || !context || !buffer || this.sources.size >= 64) return 0;
    const source = context.createBufferSource();
    const gain = context.createGain();
    source.buffer = buffer;
    gain.gain.value = volume;
    source.connect(gain).connect(this.master!);
    this.track(source, [gain], voice);
    source.start();
    return buffer.duration;
  }

  reactToHit(action: 'punchVoice' | 'uppercut' | 'weaponVoice' | 'hitVoice' = 'hitVoice'): void {
    const choices = ['ohMyGod', 'brutal', 'noPuedeSer', action] as const;
    this.bark(choices[Math.floor(Math.random() * choices.length)]);
  }

  bark(id: 'goal' | 'start' | 'punchVoice' | 'kickVoice' | 'uppercut' | 'weaponVoice' | 'hitVoice' | 'ohMyGod' | 'brutal' | 'noPuedeSer' | 'fatality'): void {
    if (!this.context || !this.ready) return;
    const now = this.context.currentTime;
    if (id !== 'goal' && now < this.goalVoiceUntil) return;
    const selected = id === 'goal' ? `goal${1 + Math.floor(Math.random() * 4)}` : id;
    if (!this.replacements.has(selected) && !this.audioBuffers.has(selected)) return;
    for (const source of this.voiceSources) {
      this.sources.delete(source);
      try { source.stop(); } catch { /* The previous voice may have ended already. */ }
    }
    this.voiceSources.clear();
    const duration = this.playAsset(selected, 0.9, true);
    if (duration) {
      this.voiceUntil = now + duration;
      if (id === 'goal') this.goalVoiceUntil = this.voiceUntil;
    }
  }

  private tone(
    type: OscillatorType,
    from: number,
    to: number,
    duration: number,
    volume: number,
    delay = 0,
  ): void {
    const context = this.context;
    const master = this.master;
    if (!this.ready || !context || !master || this.sources.size >= 64) return;
    const now = context.currentTime + delay;
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(from, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + Math.min(0.02, duration * 0.2));
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain).connect(master);
    this.track(osc, [gain]);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  private noise(
    duration: number,
    volume: number,
    filterType: BiquadFilterType,
    frequency: number,
    delay = 0,
    q = 1,
  ): void {
    const context = this.context;
    const master = this.master;
    if (!this.ready || !context || !master || !this.noiseBuffer || this.sources.size >= 64) return;
    const now = context.currentTime + delay;
    const source = context.createBufferSource();
    source.buffer = this.noiseBuffer;
    const filter = context.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = frequency;
    filter.Q.value = q;
    const gain = context.createGain();
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    source.connect(filter).connect(gain).connect(master);
    this.track(source, [filter, gain]);
    source.start(now);
    source.stop(now + duration + 0.02);
  }

  kick(power01: number): void {
    if (!this.ready) return;
    if (this.custom('shot')) return;
    this.playAsset('kick', 0.58);
    this.noise(0.12, 0.24 + power01 * 0.2, 'lowpass', 900 + power01 * 700);
    this.tone('triangle', 170 + power01 * 90, 60, 0.16, 0.3 + power01 * 0.24);
    if (power01 > 0.65) this.tone('sawtooth', 620, 90, 0.22, 0.12);
  }

  pass(): void {
    if (!this.ready) return;
    if (this.custom('pass')) return;
    this.noise(0.07, 0.2, 'lowpass', 1100);
    this.tone('triangle', 240, 120, 0.09, 0.2);
  }

  bounce(intensity: number): void {
    if (!this.ready) return;
    if (this.custom('bounce')) return;
    const v = Math.min(1, intensity);
    this.tone('sine', 150 + v * 60, 70, 0.09, 0.08 + v * 0.16);
    this.noise(0.05, 0.06 + v * 0.12, 'lowpass', 700);
  }

  post(): void {
    if (!this.ready) return;
    if (this.custom('post')) return;
    this.tone('sine', 1240, 300, 0.4, 0.3);
    this.tone('sine', 1860, 420, 0.34, 0.16, 0.01);
  }

  punchWhoosh(): void {
    this.combatSwing('punch');
  }

  punchHit(): void {
    this.combatImpact('punch');
  }

  private combatAllowed(event: string, interval: number): boolean {
    if (!this.ready || !this.context) return false;
    const now = this.context.currentTime;
    if (now - (this.combatTimes.get(event) ?? -Infinity) < interval) return false;
    this.combatTimes.set(event, now);
    return true;
  }

  combatSwing(style: AttackStyle): void {
    if (!this.combatAllowed('swing', 0.075)) return;
    if (this.custom(`swing:${style}`)) return;
    this.noise(0.14, 0.13, 'bandpass', style === 'slam' ? 280 : 1400, 0, 0.5);
    switch (style) {
      case 'punch':
        this.tone('triangle', 190, 780, 0.16, 0.15);
        break;
      case 'kick':
        this.tone('sawtooth', 90, 380, 0.2, 0.09);
        this.tone('sine', 800, 180, 0.18, 0.13, 0.08);
        break;
      case 'uppercut':
        this.tone('sine', 220, 1600, 0.38, 0.16);
        break;
      case 'spin':
        for (let i = 0; i < 5; i += 1) {
          this.noise(0.045, 0.1, 'bandpass', 650 + i * 210, i * 0.055);
          this.tone('triangle', 240 + i * 70, 100, 0.055, 0.1, i * 0.055);
        }
        break;
      case 'slam':
        this.tone('triangle', 450, 65, 0.36, 0.2);
        this.noise(0.3, 0.16, 'lowpass', 500);
        break;
    }
  }

  combatImpact(style: AttackStyle): void {
    if (!this.combatAllowed('impact', 0.07)) return;
    if (this.custom(`impact:${style}`)) return;
    this.playAsset('punch', 0.55);
    this.noise(0.1, 0.25, 'lowpass', 850);
    this.tone('sine', 155, 42, 0.24, 0.3);
    switch (style) {
      case 'punch':
        // A boxing smack followed by a wobbling rubber spring.
        this.tone('square', 330, 80, 0.07, 0.14);
        for (let i = 0; i < 4; i += 1) this.tone('sine', 190 + i * 75, 100, 0.13, 0.18 / (i + 1), 0.07 + i * 0.1);
        break;
      case 'kick':
        this.noise(0.2, 0.22, 'highpass', 1800);
        this.tone('triangle', 100, 900, 0.2, 0.16, 0.04);
        this.tone('sine', 900, 90, 0.38, 0.18, 0.24);
        break;
      case 'uppercut':
        // Slide whistle follows the victim's absurd flight.
        this.tone('sine', 280, 2000, 0.65, 0.2, 0.04);
        this.tone('triangle', 1600, 900, 0.18, 0.08, 0.65);
        break;
      case 'spin':
        for (let i = 0; i < 7; i += 1) {
          this.tone('square', 190 + i * 45, 75, 0.045, 0.1, i * 0.065);
          this.noise(0.04, 0.1, 'highpass', 1400 + i * 100, i * 0.065);
        }
        this.tone('sine', 650, 140, 0.25, 0.16, 0.42);
        break;
      case 'slam':
        this.noise(0.65, 0.3, 'lowpass', 380);
        this.tone('sine', 95, 30, 0.65, 0.3);
        this.tone('triangle', 65, 250, 0.2, 0.16, 0.15);
        this.tone('sine', 500, 85, 0.22, 0.12, 0.4);
        break;
    }
  }

  combatLanding(): void {
    if (!this.combatAllowed('landing', 0.12)) return;
    if (this.custom(`landing`)) return;
    this.noise(0.18, 0.22, 'lowpass', 500);
    this.tone('sine', 110, 38, 0.28, 0.22);
    this.tone('triangle', 90, 330, 0.12, 0.15, 0.05);
    this.tone('sine', 330, 70, 0.25, 0.13, 0.17);
  }

  weaponSwing(kind: WeaponType): void {
    if (!this.combatAllowed('weaponSwing', 0.075)) return;
    if (this.custom(`weaponSwing:${kind}`)) return;
    if (kind === 'greatsword' || kind === 'warhammer') {
      this.noise(0.45, 0.3, 'lowpass', 850);
      this.tone('triangle', 95, 40, 0.3, 0.1);
      return;
    }
    this.noise(0.22, 0.17, 'bandpass', kind === 'plank' ? 550 : 1700, 0, 0.5);
    this.tone('triangle', kind === 'bottle' ? 950 : 180, 420, 0.21, 0.12);
  }

  weaponThrow(kind: WeaponType): void {
    if (!this.combatAllowed('weaponThrow', 0.075)) return;
    if (this.custom(`weaponThrow:${kind}`)) return;
    this.noise(0.3, 0.16, 'bandpass', 1100, 0, 0.4);
    this.tone('sine', kind === 'plank' ? 350 : 1100, 130, 0.42, 0.16);
  }

  weaponImpact(kind: WeaponType, broken: boolean): void {
    if (!this.combatAllowed(`weaponImpact:${kind}`, 0.07)) return;
    if (this.custom(`${broken ? "weaponBroken" : "weaponImpact"}:${kind}`)) return;
    if (kind === 'chair') {
      this.playAsset('pipe', 0.4);
      this.noise(broken ? 0.4 : 0.18, 0.35, 'bandpass', 900);
      this.tone('triangle', 180, 45, 0.25, 0.3);
      return;
    }
    if (kind === 'warhammer') {
      this.playAsset('plank', 0.3);
      this.noise(0.4, 0.48, 'lowpass', 650);
      this.tone('sine', 95, 24, 0.45, 0.45);
      return;
    }
    if (kind === 'greatsword') {
      this.playAsset('pipe', 0.32);
      this.noise(0.32, 0.45, 'lowpass', 1100);
      this.tone('sine', 100, 28, 0.35, 0.4);
      return;
    }
    this.playAsset(kind === 'bottle' && broken ? 'bottle' : kind, 0.55);
    this.tone('sine', 145, 42, 0.2, 0.25);
    if (kind === 'pipe' || kind === 'shotgun') {
      // Inharmonic partials make the pipe ring like an oversized cartoon bell.
      for (const [frequency, volume] of [[580, 0.2], [1363, 0.14], [2378, 0.09]]) {
        this.tone('sine', frequency, frequency * 0.93, broken ? 0.65 : 0.48, volume);
      }
      this.noise(0.07, 0.16, 'highpass', 2700);
    } else if (kind === 'plank') {
      this.noise(broken ? 0.28 : 0.12, 0.28, 'bandpass', 1250, 0, 0.5);
      this.tone('triangle', 260, 85, 0.21, 0.24);
      if (broken) {
        for (let i = 0; i < 4; i += 1) this.noise(0.05, 0.14 / (i + 1), 'highpass', 1800, 0.06 + i * 0.055);
      }
    } else {
      this.noise(broken ? 0.34 : 0.07, 0.22, 'highpass', 2500);
      for (let i = 0; i < (broken ? 6 : 2); i += 1) {
        this.tone('sine', 1900 + i * 437, 1500 + i * 310, 0.12, 0.13 / (1 + i * 0.5), i * 0.045);
      }
    }
  }

  slide(): void {
    if (!this.ready) return;
    if (this.custom('slide')) return;
    this.noise(0.3, 0.2, 'highpass', 900);
  }

  explosion(): void {
    if (!this.ready) return;
    this.explosionLayer(this.nextExplosionSecond ? 'explosion2' : 'explosion');
    this.nextExplosionSecond = !this.nextExplosionSecond;
  }

  private explosionLayer(id: 'explosion' | 'explosion2'): void {
    if (!this.ready) return;
    if (this.custom(id)) return;
    this.bombExplosion();
  }

  bombExplosion(): void {
    if (!this.ready) return;
    this.noise(0.55, 0.62, 'lowpass', 320);
    this.tone('sine', 120, 32, 0.5, 0.5);
    this.noise(0.2, 0.3, 'highpass', 1400, 0.02);
  }

  shotgun(): void {
    if (!this.ready || this.custom('shotgun')) return;
    if (this.audioBuffers.has('shotgun')) { this.playAsset('shotgun', 0.9); return; }
    this.noise(0.2, 0.5, 'lowpass', 1800);
    this.tone('triangle', 180, 35, 0.22, 0.4);
    this.noise(0.06, 0.25, 'highpass', 2200);
  }

  rocketLaunch(): void {
    if (!this.ready || this.custom('rocketLaunch')) return;
    if (this.audioBuffers.has('rocketLaunch')) { this.playAsset('rocketLaunch', 0.8); return; }
    this.noise(0.8, 0.5, 'lowpass', 2200);
    this.noise(0.12, 0.3, 'lowpass', 450);
  }

  rocketExplosion(_x = 0, _y = 0, _z = 0): void {
    if (!this.ready || this.custom('rocketExplosion')) return;
    if (this.audioBuffers.has('rocketExplosion')) { this.playAsset('rocketExplosion', 0.85); return; }
    this.bombExplosion();
  }

  pickup(): void {
    if (!this.ready) return;
    if (this.custom('pickup')) return;
    this.tone('triangle', 480, 900, 0.1, 0.22);
    this.tone('triangle', 720, 1320, 0.12, 0.16, 0.07);
  }

  menuClick(): void {
    if (!this.ready) {
      void this.unlock().then(() => { if (this.ready) this.menuClick(); }).catch(() => undefined);
      return;
    }
    if (this.custom('menuClick')) return;
    this.tone('sine', 700, 1050, 0.06, 0.16);
    this.tone('triangle', 1200, 800, 0.045, 0.06, 0.015);
  }

  whistle(kind: 'short' | 'double' | 'long'): void {
    if (!this.ready) return;
    if (this.custom(`whistle:${kind}`)) return;
    const burst = (delay: number, duration: number) => {
      this.tone('square', 2350, 2250, duration, 0.14, delay);
      this.tone('square', 3140, 3020, duration, 0.08, delay);
    };
    if (kind === 'short') burst(0, 0.14);
    else if (kind === 'double') {
      burst(0, 0.1);
      burst(0.16, 0.22);
    } else burst(0, 0.7);
  }

  goalHorn(): void {
    if (!this.ready) return;
    if (this.custom('horn')) return;
    this.tone('sawtooth', 220, 218, 0.7, 0.3);
    this.tone('sawtooth', 277, 274, 0.7, 0.24);
    this.tone('sawtooth', 165, 163, 0.9, 0.26, 0.05);
    this.noise(1.4, 0.34, 'bandpass', 500, 0, 0.4);
  }

  down(): void {
    if (!this.ready) return;
    if (this.custom('down')) return;
    this.tone('sine', 320, 70, 0.3, 0.34);
    this.tone('sine', 90, 42, 0.24, 0.3);
    this.noise(0.2, 0.26, 'lowpass', 460);
  }

  chargeStart(): void {
    const context = this.context;
    const master = this.master;
    if (!this.ready || !context || !master || this.chargeOsc || this.customCharge || this.sources.size >= 64) return;
    const replacement = this.replacements.get('charge');
    if (replacement) {
      this.customCharge = context.createBufferSource();
      this.customCharge.buffer = replacement;
      this.customCharge.loop = true;
      this.customCharge.connect(master);
      this.track(this.customCharge, []);
      this.customCharge.start();
      return;
    }
    this.chargeOsc = context.createOscillator();
    this.chargeGain = context.createGain();
    this.chargeOsc.type = 'sawtooth';
    this.chargeOsc.frequency.value = 160;
    this.chargeGain.gain.value = 0.0001;
    this.chargeOsc.connect(this.chargeGain).connect(master);
    this.track(this.chargeOsc, [this.chargeGain]);
    this.chargeOsc.start();
  }

  chargeUpdate(ratio: number): void {
    const context = this.context;
    if (!context || !this.chargeOsc || !this.chargeGain) return;
    const now = context.currentTime;
    this.chargeOsc.frequency.setTargetAtTime(150 + ratio * 420, now, 0.05);
    this.chargeGain.gain.setTargetAtTime(0.05 + ratio * 0.1, now, 0.08);
  }

  chargeStop(): void {
    if (this.customCharge) { this.customCharge.stop(); this.customCharge = null; }
    const context = this.context;
    if (!context || !this.chargeOsc || !this.chargeGain) return;
    const now = context.currentTime;
    this.chargeGain.gain.setTargetAtTime(0.0001, now, 0.04);
    const osc = this.chargeOsc;
    osc.stop(now + 0.25);
    this.chargeOsc = null;
    this.chargeGain = null;
  }

  dispose(): void {
    this.disposed = true;
    this.removeUnlockListeners();
    window.removeEventListener('pointerdown', this.resumeMusicFromGesture);
    window.removeEventListener('keydown', this.resumeMusicFromGesture);
    this.chargeStop();
    for (const source of this.sources) {
      source.onended = null;
      try { source.stop(); } catch { /* Source may have already ended. */ }
      source.disconnect();
    }
    this.sources.clear();
    this.combatTimes.clear();
    this.voiceSources.clear();
    this.audioBuffers.clear();
    this.replacements.clear();
    this.stopBackground();
    this.stopMusic();
    for (const url of this.musicUrls.values()) URL.revokeObjectURL(url);
    this.musicUrls.clear();
    if (this.background) { this.background.removeAttribute('src'); this.background.load(); this.background = null; }
    void this.context?.close().catch(() => undefined);
    this.context = null;
    this.master = null;
    this.noiseBuffer = null;
  }
}
